
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import PositHeader from "@/components/posit/PositHeader";
import PositSummary from "@/components/posit/PositSummary";
import PositActions from "@/components/posit/PositActions";
import PositDashboard from "@/components/posit/PositDashboard";
import PositDeleted from "@/components/posit/PositDeleted";
import InvalidPositIdError from "@/components/debug/InvalidPositIdError";
import { usePositValidation } from "@/hooks/usePositValidation";
import DashboardLayout from "@/components/layout/DashboardLayout";

/**
 * MyPosit.tsx
 * 
 * Individual Posit view page with management features.
 * Shows detailed information about a specific Posit including
 * analytics, content, and management options.
 */

const MyPosit = () => {
  const { positId: rawPositId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isValidPositId, positId } = usePositValidation(rawPositId);

  // Fetch posit data
  const { data: posit, isLoading: isLoadingPosit } = useQuery({
    queryKey: ["posit", positId],
    queryFn: async () => {
      if (!positId) return null;

      const { data, error } = await supabase
        .from("posits")
        .select(`
          *,
          users!fk_posits_user_id (
            id,
            first_name,
            last_name,
            handle,
            subscription_type,
            trial_end_date
          )
        `)
        .eq("id", positId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!positId && isValidPositId,
  });

  // Fetch user profile for premium status
  const { data: userProfile } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("users")
        .select("subscription_type, trial_end_date")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      
      const isTrialValid = data?.trial_end_date ? new Date(data.trial_end_date) > new Date() : false;
      const isPremiumOrTrial = data?.subscription_type === 'posit_plus' || 
                              (data?.subscription_type === 'trial' && isTrialValid);
      
      return {
        ...data,
        isPremium: isPremiumOrTrial
      };
    },
    enabled: !!user?.id,
  });

  // Update privacy mutation
  const updatePrivacyMutation = useMutation({
    mutationFn: async (privacy: 'public' | 'private' | 'exclusive') => {
      if (!posit?.id) throw new Error("No posit ID");

      const { error } = await supabase
        .from("posits")
        .update({ privacy })
        .eq("id", posit.id);

      if (error) throw error;
      return privacy;
    },
    onSuccess: (privacy) => {
      queryClient.invalidateQueries({ queryKey: ["posit", positId] });
      toast({
        title: "Privacy Updated",
        description: `Your Posit is now ${privacy}.`,
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update privacy settings. Please try again.",
      });
    },
  });

  const handleDelete = async () => {
    try {
      if (!posit?.id) return;

      // Use secure edge function for complete deletion (includes vector cleanup)
      const { deletePositSecurely } = await import('../utils/secureApiCalls');
      
      const result = await deletePositSecurely(posit.id);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      toast({
        title: "Success",
        description: "Posit deleted successfully ",
      });

      // Always navigate to the regular Posits page
      navigate('/posits');

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete the Posit. Please try again.",
      });
    }
  };

  // Show error if posit ID is invalid
  if (rawPositId && !isValidPositId) {
    return <InvalidPositIdError positId={rawPositId} showDetails={true} />;
  }

  if (isLoadingPosit) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!posit) {
    return (
      <PositDeleted
        context="view"
        isAuthenticated={!!user}
        positId={positId}
      />
    );
  }

  const isPremium = userProfile?.isPremium || false;

  return (
    <DashboardLayout
      title={posit.title}
      description={posit.posit_description || "Manage your Posit"}
      maxWidth="xl"
    >
      <div className="space-y-6 md:space-y-8">
        <PositHeader 
          posit={posit} 
          isPremium={isPremium} 
          onUpdatePrivacy={(privacy) => updatePrivacyMutation.mutate(privacy)}
        />

        <PositSummary 
          summary={posit.summary} 
          summaryLight={posit.summary_light}
        />
        
        <div className="border-t pt-6">
          <PositActions 
            positId={posit.id} 
            posit={posit}
            isPremium={isPremium} 
            onDelete={handleDelete} 
          />
        </div>
        
        <PositDashboard 
          positId={posit.id} 
          isPremium={isPremium}
        />
      </div>
    </DashboardLayout>
  );
};

export default MyPosit;
